/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { codeBlockButtonMessages } from '@atlaskit/editor-common/messages';
import type { SelectOption } from '@atlaskit/editor-common/types';
import { akEditorLineHeight } from '@atlaskit/editor-shared-styles';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box } from '@atlaskit/primitives/compiled';
import { components } from '@atlaskit/react-select/components';
import { PopupSelect, type PopupSelectProps } from '@atlaskit/select/popup-select';
import type { GroupProps, OptionProps, ValueType } from '@atlaskit/select/types';
import { token } from '@atlaskit/tokens';

import {
	createGroupedLanguageOptions,
	type LanguagePickerOption,
	type LanguagePickerOptionGroup,
	type LanguagePickerSelectionSource,
} from './language-picker-options';

export type LanguagePickerProps = {
	defaultValue?: LanguagePickerOption;
	filterOption: (option: SelectOption<LanguagePickerOption>, rawInput: string) => boolean;
	formatMessage: IntlShape['formatMessage'];
	languagePickerOptions: LanguagePickerOption[];
	onMenuOpen?: () => void;
	onSelection: (
		option: LanguagePickerOption,
		selectionSource: LanguagePickerSelectionSource,
		interactionMethod?: LanguagePickerInteractionMethod,
	) => void;
	recentLanguageValues?: string[];
	triggerSpacing?: 'default' | 'compact';
};

export type LanguagePickerInteractionMethod = 'keyboard' | 'mouse';

const pickerOptionStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	lineHeight: akEditorLineHeight,
});

const styles = cssMap({
	divider: {
		width: '100%',
		borderBlockEnd: 'none',
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		borderInline: 'none',
	},

	trigger: {
		maxWidth: '200px',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		button: {
			textAlign: 'left',

			'&::after': {
				// Remove blue outline when picker is open
				content: 'none',
			},
		},
	},
});

const CustomGroup = (props: GroupProps<LanguagePickerOption, false>): React.JSX.Element => {
	const allGroups = props.selectProps.options as LanguagePickerOptionGroup[];
	const isFirstGroup = allGroups.length > 0 && allGroups[0] === props.data;

	return (
		<Fragment>
			{!isFirstGroup && <Box as="hr" xcss={styles.divider} role="presentation" />}
			{/* eslint-disable-next-line react/jsx-props-no-spreading -- react-select custom components must forward all props to the default Group. */}
			<components.Group {...props} />
		</Fragment>
	);
};

const CustomOption = (props: OptionProps<LanguagePickerOption, false>): React.JSX.Element => {
	return (
		// eslint-disable-next-line react/jsx-props-no-spreading -- react-select custom components must forward all props to the default Option.
		<components.Option {...props}>
			<span css={pickerOptionStyles}>{props.children}</span>
		</components.Option>
	);
};
const popupSelectComponents = { Group: CustomGroup, Option: CustomOption };

type PopupSelectTarget = NonNullable<PopupSelectProps<LanguagePickerOption>['target']>;
type PopupSelectTargetProps = Parameters<PopupSelectTarget>[0];
type PopupSelectPopperProps = NonNullable<PopupSelectProps['popperProps']>;
type PopupSelectPopperPlacement = PopupSelectPopperProps['placement'];

const focusWithoutScrolling = (element: HTMLElement) => {
	element.focus({ preventScroll: true });
};

const getRecentlyUsedLanguages = (
	recentLanguageValues: string[],
	optionsByValue: Map<string, LanguagePickerOption>,
): LanguagePickerOption[] => {
	const recentlyUsedLanguages: LanguagePickerOption[] = [];

	for (const recentLanguageValue of recentLanguageValues) {
		const option = optionsByValue.get(recentLanguageValue);

		if (option) {
			recentlyUsedLanguages.push(option);
		}
	}

	return recentlyUsedLanguages;
};

export const LanguagePicker = ({
	defaultValue,
	filterOption,
	formatMessage,
	languagePickerOptions,
	recentLanguageValues = [],
	onMenuOpen,
	onSelection,
	triggerSpacing = 'default',
}: LanguagePickerProps): React.JSX.Element => {
	const label = defaultValue?.label ?? formatMessage(codeBlockButtonMessages.selectLanguage);
	const selectLanguageLabel = formatMessage(codeBlockButtonMessages.selectLanguage);
	const [hasSearchQuery, setHasSearchQuery] = useState(false);
	const [lockedPopperPlacement, setLockedPopperPlacement] = useState<PopupSelectPopperPlacement>();
	const inputValueRef = useRef('');
	// PopupSelect restores focus based on the trigger/open interaction, not the option
	// interaction. Keep this tied to the trigger so keyboard-open + mouse-select still follows
	// the keyboard focus path.
	const interactionMethodRef = useRef<LanguagePickerInteractionMethod>('keyboard');
	const optionsByValue = useMemo(
		() => new Map(languagePickerOptions.map((option) => [option.value, option])),
		[languagePickerOptions],
	);
	const recentlyUsedLanguages = useMemo(
		() => getRecentlyUsedLanguages(recentLanguageValues, optionsByValue),
		[recentLanguageValues, optionsByValue],
	);
	const options = useMemo<LanguagePickerOptionGroup[]>(
		() =>
			createGroupedLanguageOptions({
				formatMessage,
				languages: languagePickerOptions,
				recentlyUsedLanguages,
			}),
		[formatMessage, languagePickerOptions, recentlyUsedLanguages],
	);
	const searchOptions = useMemo(() => options.flatMap((group) => group.options), [options]);
	const stableMenuPopperProps = useMemo<PopupSelectPopperProps>(
		() => ({
			// Allow Popper to choose top on the first open when bottom has no room, then lock the
			// chosen placement so later hover/search renders do not move the open picker.
			placement: lockedPopperPlacement ?? 'bottom-start',
			modifiers: [
				{ name: 'offset', options: { offset: [0, 8] } },
				{ name: 'preventOverflow', enabled: false },
				...(lockedPopperPlacement ? [{ name: 'flip', enabled: false } as const] : []),
			],
			onFirstUpdate: ({ placement }) => {
				setLockedPopperPlacement(placement);
			},
		}),
		[lockedPopperPlacement],
	);
	const handleChange = useCallback(
		(option: ValueType<LanguagePickerOption>) => {
			if (!option) {
				return;
			}

			const isSearchSelection = inputValueRef.current.trim().length > 0;
			const selectionSource: LanguagePickerSelectionSource = isSearchSelection
				? 'search'
				: (option.selectionSource ?? 'all');

			onSelection(option, selectionSource, interactionMethodRef.current);
		},
		[onSelection],
	);
	const handleInputChange = useCallback(
		(newInputValue: string, actionMeta?: { action?: string }) => {
			// React-select clears the input as part of selecting a value before onChange fires.
			// Keep the last user-typed query so handleChange can report search selections correctly.
			const isInputChange = !actionMeta || actionMeta.action === 'input-change';
			if (isInputChange) {
				inputValueRef.current = newInputValue;
				setHasSearchQuery(newInputValue.trim().length > 0);
				return newInputValue;
			}
			return inputValueRef.current;
		},
		[],
	);
	const handleMenuOpen = useCallback(() => {
		inputValueRef.current = '';
		setHasSearchQuery(false);
		setLockedPopperPlacement(undefined);
		onMenuOpen?.();
	}, [onMenuOpen]);
	const handleTriggerMouseUp = useCallback((event: React.MouseEvent<HTMLElement>) => {
		interactionMethodRef.current = 'mouse';
		// Focus the trigger before PopupSelect opens so FocusLock restores focus to the
		// trigger after a mouse selection. Relying on the button's default mouse focus is too
		// late here; FocusLock can still remember the code block as the previous focus target.
		focusWithoutScrolling(event.currentTarget);
	}, []);
	const handleTriggerKeyDownCapture = useCallback(() => {
		interactionMethodRef.current = 'keyboard';
	}, []);
	const renderTarget = useCallback(
		({
			isOpen,
			ref,
			onKeyDown,
			'aria-controls': ariaControls,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHasPopup,
		}: PopupSelectTargetProps) => (
			<div css={styles.trigger}>
				<Button
					spacing={triggerSpacing}
					shouldFitContainer
					onMouseUp={handleTriggerMouseUp}
					onKeyDownCapture={handleTriggerKeyDownCapture}
					onKeyDown={onKeyDown}
					ref={ref}
					iconAfter={ChevronDownIcon}
					appearance="subtle"
					isSelected={isOpen}
					aria-controls={ariaControls}
					aria-expanded={ariaExpanded}
					aria-haspopup={ariaHasPopup}
					testId="code-block-language-picker-trigger"
				>
					{label}
				</Button>
			</div>
		),
		[label, triggerSpacing, handleTriggerMouseUp, handleTriggerKeyDownCapture],
	);

	return (
		<PopupSelect<LanguagePickerOption>
			components={popupSelectComponents}
			filterOption={filterOption}
			label={selectLanguageLabel}
			maxMenuHeight={300}
			minMenuWidth={200}
			menuPlacement="auto"
			onChange={handleChange}
			onInputChange={handleInputChange}
			onMenuOpen={handleMenuOpen}
			options={hasSearchQuery ? searchOptions : options}
			popperProps={stableMenuPopperProps}
			searchThreshold={-1}
			target={renderTarget}
			testId="code-block-language-picker"
			defaultValue={defaultValue}
		/>
	);
};
